/**
 * Aimeta Web 运行时平台识别。
 *
 * 判定优先级（须先于 chrome.webview 判断手机端）：
 * 1. mobile — aimeta_android 注入 `__AIMETA_IS_NATIVE__` / `AimetaNative`（WebView 垫片也会模拟 chrome.webview）
 * 2. windows — 真实 WebView2 桌面客户端（`window.chrome.webview`，且无原生标记）
 * 3. browser — 普通浏览器访问
 *
 * UI 布局仅按上述平台分类，不使用视口宽度断点。
 */

export type AimetaRuntimePlatform = 'mobile' | 'windows' | 'browser';

function getWindow(): Window | undefined {
  return typeof window !== 'undefined' ? window : undefined;
}

/** Android Aimeta App WebView（aimeta_android 在页面加载时注入） */
export function isAimetaAndroidWebView(win: Window = getWindow()!): boolean {
  if (!win) return false;
  const w = win as AimetaWindow;
  return w.__AIMETA_IS_NATIVE__ === true || !!w.AimetaNative;
}

/**
 * 系统 AimetaManager 是否可用（由 aimeta_android 注入 __AIMETA_SYSTEM_REACHABLE）。
 * 不可用表示须走 Web 内登录页，而非 Settings 账号中心。
 */
export function isAimetaManagerReachable(win: Window = getWindow()!): boolean {
  if (!isAimetaAndroidWebView(win)) return false;
  return !!(win as AimetaWindow).__AIMETA_SYSTEM_REACHABLE;
}

/** 手机 App 且无 AimetaManager：未登录时必须进入 Web 登录页 */
export function shouldUseWebLoginOnMobile(win: Window = getWindow()!): boolean {
  return isAimetaAndroidWebView(win) && !isAimetaManagerReachable(win);
}

/**
 * 会员/积分是否以 account_info_v2 为准（由 aimeta_client_web 请求，与浏览器/Windows 壳相同）。
 * Android 仅注入 token/deviceCode，不在原生侧拉积分。
 */
export function shouldUseServerActivationState(win: Window = getWindow()!): boolean {
  return true;
}

/** Windows 桌面客户端 WebView2 */
export function isAimetaWindowsClient(win: Window = getWindow()!): boolean {
  if (!win) return false;
  if (isAimetaAndroidWebView(win)) return false;
  return !!(win as AimetaWindow).chrome?.webview;
}

/** 普通浏览器（非 App、非桌面壳） */
export function isAimetaBrowser(win: Window = getWindow()!): boolean {
  if (!win) return true;
  return detectAimetaPlatform(win) === 'browser';
}

/** 与业务命名一致：是否在普通浏览器中访问 */
export const isWeb = isAimetaBrowser;

/**
 * 当前运行时平台：手机 App WebView / Windows 客户端 / 浏览器。
 */
export function detectAimetaPlatform(win: Window = getWindow()!): AimetaRuntimePlatform {
  if (!win) return 'browser';
  if (isAimetaAndroidWebView(win)) return 'mobile';
  if ((win as AimetaWindow).chrome?.webview) return 'windows';
  return 'browser';
}

/** 与历史命名一致：桌面 WebView2 客户端 */
export function isDesktopApp(win: Window = getWindow()!): boolean {
  return isAimetaWindowsClient(win);
}

/**
 * 是否采用移动端 UI 布局（仅运行时平台，不看窗口宽度 / UA）。
 * - mobile（Android App WebView）：true
 * - windows / browser：false
 */
export function shouldUseMobileLayout(win: Window = getWindow()!): boolean {
  return detectAimetaPlatform(win) === 'mobile';
}

/** 桌面壳布局：Windows 客户端与浏览器一致（非 Android App） */
export function isDesktopShell(win: Window = getWindow()!): boolean {
  const platform = detectAimetaPlatform(win);
  return platform === 'windows' || platform === 'browser';
}

/** Vite 开发模式（npm run dev） */
export function isViteDevMode(): boolean {
  return import.meta.env.DEV;
}

/** 开发调试：运行时平台简短文案（App / Win / 浏览器） */
export function getRuntimePlatformLabel(win: Window = getWindow()!): string {
  const names: Record<AimetaRuntimePlatform, string> = {
    mobile: 'App',
    windows: 'Win',
    browser: '浏览器',
  };
  return names[detectAimetaPlatform(win)];
}

type WebViewHostObjects = NonNullable<
  NonNullable<AimetaWindow['chrome']>['webview']
>['hostObjects'];

/**
 * chrome.webview.hostObjects（Windows 客户端 + Android WebView2 垫片）。
 * 用于 JsManager.getConfig、GetDeviceCode 等跨端宿主 API。
 */
export function getWebViewHostObjects(win: Window = getWindow()!): WebViewHostObjects | undefined {
  if (!win || isAimetaBrowser(win)) return undefined;
  return (win as AimetaWindow).chrome?.webview?.hostObjects;
}

/**
 * WebView2 hostObjects：Windows 桌面客户端，或 Android App 内嵌垫片（CallsStartupVoice 等）。
 */
export function getWebView2HostObjects(win: Window = getWindow()!): WebViewHostObjects | undefined {
  if (!win || isAimetaBrowser(win)) return undefined;
  if (isAimetaAndroidWebView(win) || isAimetaWindowsClient(win)) {
    return (win as AimetaWindow).chrome?.webview?.hostObjects;
  }
  return undefined;
}

/** Android App JS Bridge（仅 Aimeta WebView） */
export function getAimetaNativeBridge(
  win: Window = getWindow()!
): AimetaWindow['AimetaNative'] | undefined {
  if (!isAimetaAndroidWebView(win)) return undefined;
  return (win as AimetaWindow).AimetaNative;
}

export interface AimetaWindow extends Window {
  __AIMETA_IS_NATIVE__?: boolean;
  __AIMETA_WEBVIEW2_SHIM__?: boolean;
  __AIMETA_TOKEN?: string;
  __AIMETA_ACCOUNT_NAME?: string;
  __AIMETA_DEVICE_ACTIVATED?: boolean;
  __AIMETA_VIP?: boolean;
  __AIMETA_EXPIRE_TIME?: number;
  __AIMETA_SERVER_URL?: string;
  __AIMETA_SYSTEM_REACHABLE?: boolean;
  __AIMETA_DEVICE_CODE?: string;
  __AIMETA_DEVICE_NAME?: string;
  __AIMETA_CAMERA_INJECTION?: boolean;
  __AIMETA_MIC_INJECTION?: boolean;
  __AIMETA_APP_VERSION_CODE?: number;
  __AIMETA_APP_VERSION_NAME?: string;
  AimetaNative?: {
    getDeviceInfo?: () => string;
    getDeviceCode?: () => string;
    getDeviceName?: () => string;
    openPlatformManagement?: () => void;
    openKnowledge?: () => void;
    openSettings?: () => void;
    openDevPanel?: () => void;
    openSystemAccountLogin?: () => void;
    getAppVersion?: () => string;
    checkAppUpdate?: () => string;
    startAppUpdate?: (updateJson: string) => string;
    cancelAppUpdate?: () => string;
    presentAppUpdateUi?: (mode: string) => string;
    exitApp?: () => void;
  };
  chrome?: {
    webview?: {
      hostObjects?: Record<string, unknown>;
    };
  };
}
